import * as Y from 'yjs';
import { IndexeddbPersistence } from 'y-indexeddb';
import { UserPersistence } from './user-persistence.js';
import { t } from '../../shared/i18n/index.js';
import { notify } from '../host.js';
import { NOTICE } from '../notices.js';

const live = new Set();
let listening = false;

function register(provider) {
  live.add(provider);
  if (listening || typeof document === 'undefined' || typeof window === 'undefined') return;
  listening = true;
  const save = () => {
    document.activeElement?.blur();
    flushPendingWrites().catch(error => console.error('[WebStorage] Save failed:', error));
  };
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') save(); });
  window.addEventListener('pagehide', save);
}

/** Normal edits still use y-indexeddb. An explicit save waits for a committed,
 * additive snapshot, so a rejected write cannot masquerade as a finished save.
 */
export class DocumentPersistence extends IndexeddbPersistence {
  constructor(name, doc) {
    super(name, doc);
    this._revision = 1;
    this._savedRevision = 0;
    this._flushing = null;
    this._changed = (_bytes, origin) => { if (origin !== this) this._revision++; };
    doc.on('update', this._changed);
    register(this);
  }

  flush() {
    if (this._flushing) return this._flushing;
    this._flushing = this._flush().catch(error => {
      this.emit('error', [error]);
      throw error;
    }).finally(() => { this._flushing = null; });
    return this._flushing;
  }

  // deleteDocCache() is about to delete this database on purpose (e.g. after
  // a revoked map's content was recovered into a new map). Stop saving into
  // it: the deletion closes the connection, and a save attempt would then
  // read as a storage failure and block all editing.
  discard() {
    this._discarded = true;
  }

  async _flush() {
    if (this._destroyed || this._discarded) return;
    const [db] = await Promise.all([this._db, this.whenSynced]);
    while (this._savedRevision < this._revision) {
      const revision = this._revision;
      await new Promise((resolve, reject) => {
        const tx = db.transaction('updates', 'readwrite');
        // Append; never replace another tab's updates or deletion tombstones.
        tx.objectStore('updates').add(Y.encodeStateAsUpdate(this.doc));
        tx.oncomplete = resolve;
        tx.onabort = () => reject(tx.error || new Error('Document save aborted'));
        tx.onerror = () => reject(tx.error || new Error('Document save failed'));
      });
      this._savedRevision = revision;
    }
  }

  async destroy() {
    if (this._destroyed) return;
    await this.flush();
    this.doc.off('update', this._changed);
    await super.destroy();
    live.delete(this);
  }
}

/** Mark live providers of one document database as discarded (see discard()). */
export function discardDocPersistence(name) {
  for (const provider of live) {
    if (provider.name === name && typeof provider.discard === 'function') provider.discard();
  }
}

export class VaultPersistence extends UserPersistence {
  constructor(userId, doc) { super(userId, doc); register(this); }
  async destroy() { await super.destroy(); live.delete(this); }
}

export async function flushPendingWrites() {
  let providers;
  do {
    providers = [...live].filter(provider => !provider._destroyed);
    const results = await Promise.allSettled(providers.map(provider => provider.flush()));
    const failures = results.filter(result => result.status === 'rejected');
    if (failures.length) throw new AggregateError(failures.map(result => result.reason), 'Could not save all local data');
  } while ([...live].some(provider => !provider._destroyed && !providers.includes(provider)));
}

export async function reloadApp(beforeReload) {
  document.activeElement?.blur();
  try { await flushPendingWrites(); }
  catch (error) {
    console.error('[WebStorage] Reload cancelled:', error);
    notify({ code: NOTICE.SAVE_FAILED, level: 'error', message: t('sync.save.failedWeb'), data: { before: 'reload' } });
    return false;
  }
  await beforeReload?.();
  window.location.reload();
  return true;
}
